import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { ClipSpinner } from '~/components/ui/spinners/ClipSpinner';
import { cn } from '~/utils';

export type IButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> & {
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  width?: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'accent'
    | 'conversion'
    | 'ghost-primary'
    | 'destructive-outline'
    | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  outline?: boolean;
};

export function Button({
  children,
  width,
  loading,
  className,
  onClick,
  type,
  disabled: disabledProp,
  variant: variantProp,
  size: sizeProp,
  ...rest
}: IButtonProps) {
  const disabled = disabledProp || loading;
  const variant = variantProp || 'primary';
  const size = sizeProp || 'md';

  const variantClasses: Record<string, string> = {
    primary: 'bg-prim text-neut-0 hover:bg-prim-hover',
    secondary:
      'bg-neut-bg text-neut-prim hover:bg-neut-hover outline-2 outline-neut-border',
    ghost: 'bg-transparent text-neut-prim hover:bg-neut-hover',
    accent:
      'bg-neut-0 border border-neut-400 text-neut-sec hover:bg-neut-hover',
    conversion:
      'bg-warning-500 text-neut-0 hover:bg-warning-600 disabled:bg-warning-300',
    'ghost-primary': 'bg-transparent text-prim hover:text-prim-hover',
    'destructive-outline':
      'bg-transparent text-error-600 border border-error-600 hover:bg-error-100',
    destructive:
      'bg-error-600 text-neut-0 hover:bg-error-800 disabled:bg-error-300',
  };

  const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'py-2 px-3',
    md: 'py-3 px-4',
    lg: 'py-4 px-6',
  };

  return (
    <button
      type={type || 'button'}
      disabled={disabled || loading}
      style={{ width: width ? width : '100%' }}
      onClick={onClick}
      className={cn(
        'text-sm font-medium rounded-md transition-colors duration-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 flex justify-center items-center gap-2',
        sizeClasses[size],
        variantClasses[variant],
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className
      )}
      {...rest}
    >
      {loading ? (
        <div className="flex gap-2 justify-center items-center ">
          {children} <ClipSpinner />
        </div>
      ) : (
        children
      )}
    </button>
  );
}
